import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import StatementTableView from './StatementTableView';
import { FinancialStatementService, StatementTable } from '../../services/financialStatement.service';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';

const NotesPack: React.FC = () => {
    const toast = useToast();
    const [asOf, setAsOf] = useState(toLocalIsoDate());
    const [applied, setApplied] = useState<string | null>(null);
    const [table, setTable] = useState<StatementTable | null>(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setTable(await FinancialStatementService.getNotes(asOf));
            setApplied(asOf);
        } catch (err) {
            toast.showApiError(err, 'Failed to load the notes pack');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [asOf]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const download = async (format: 'pdf' | 'xlsx') => {
        if (!applied) return;
        try {
            await FinancialStatementService.downloadNotes(applied, format);
        } catch (err) {
            toast.showApiError(err, 'Download failed');
        }
    };

    return (
        <Box data-help-page="accounting.notesPack.page" sx={{ p: 2.5 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>Notes to the Financial Statements</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 3 }}>
                <TextField data-help="accounting.notesPack.asOf" label="As of" type="date" size="small" value={asOf} onChange={e => setAsOf(e.target.value)}
                    InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 160px', maxWidth: 200 }} />
                <Button data-help="accounting.notesPack.apply" variant="contained" onClick={load} disabled={loading}>{loading ? 'Loading...' : 'Apply'}</Button>
                <Button data-help="accounting.notesPack.pdf" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('pdf')} disabled={!applied}>PDF</Button>
                <Button data-help="accounting.notesPack.excel" variant="outlined" startIcon={<FileDownloadIcon />} onClick={() => download('xlsx')} disabled={!applied}>Excel</Button>
            </Box>
            {table && <StatementTableView table={table} />}
        </Box>
    );
};

export default NotesPack;
